import React, { useEffect, useMemo, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import BookAppointmentDialog from '../appointments/BookAppointmentDialog';
import { formatCalendarDate } from '../../utils/dateFormatUtils';
import { DEFAULT_CURRENCY, formatCurrency } from '../../utils/currencyUtils';
import StoreService from '../../services/storeService';
import TeamMemberService from '../../services/teamMemberService';
import { getStoreWaitlistPath } from '../../pages/stores/storeHubTabs';

const EMPTY_SERVICE_IDS = [];
const EMPTY_APPOINTMENTS = [];

const WaitlistStaffAlertDialog = ({ alert, onClose, storeId, storeCurrency }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookOpen, setBookOpen] = useState(false);
  const [storeDetails, setStoreDetails] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const resolvedStoreId = alert?.storeId || storeId;
  const bookingCustomer = useMemo(
    () => (alert ? { customerId: alert.customerId, name: alert.customerName } : null),
    [alert],
  );
  const bookingServiceIds = alert?.serviceIds || EMPTY_SERVICE_IDS;

  const isSlotOpened = alert?.alertType === 'slotOpened';
  const title = t(isSlotOpened ? 'waitlistAlert.slotOpenedTitle' : 'waitlistAlert.joinedTitle');

  useEffect(() => {
    if (!bookOpen || !resolvedStoreId) return undefined;
    let cancelled = false;
    Promise.all([
      StoreService.getStore(resolvedStoreId).catch(() => null),
      TeamMemberService.getTeamMembersByStore(resolvedStoreId).catch(() => []),
    ]).then(([store, members]) => {
      if (cancelled) return;
      setStoreDetails(store);
      setTeamMembers(Array.isArray(members) ? members : []);
    });
    return () => {
      cancelled = true;
    };
  }, [bookOpen, resolvedStoreId]);

  return (
    <>
      <ScrollableDialog
        open={Boolean(alert) && !bookOpen}
        onClose={onClose}
        maxWidth="md"
        showCloseButton
        dividers
        title={title}
        titleSx={{ pb: 1, flexShrink: 0 }}
        actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
        actions={(
          <>
            <Button
              color="inherit"
              onClick={() => {
                onClose?.();
                if (resolvedStoreId) {
                  navigate(getStoreWaitlistPath(resolvedStoreId));
                }
              }}
            >
              {t('waitlistAlert.viewWaitlist')}
            </Button>
            <PrimaryActionButton onClick={() => setBookOpen(true)}>
              {t('waitlistAlert.book')}
            </PrimaryActionButton>
          </>
        )}
      >
        <Stack spacing={1.5}>
          <Typography><strong>{t('waitlistAlert.customer')}:</strong> {alert?.customerName}</Typography>
          <Typography><strong>{t('waitlistAlert.services')}:</strong> {(alert?.serviceNames || []).join(', ')}</Typography>
          <Typography><strong>{t('waitlistAlert.duration')}:</strong> {t('waitlistAlert.durationValue', { minutes: alert?.durationMinutes })}</Typography>
          <Typography>
            <strong>{t('waitlistAlert.value')}:</strong>{' '}
            {formatCurrency(alert?.estimatedAmountDue, storeCurrency || DEFAULT_CURRENCY)}
          </Typography>
          {isSlotOpened && alert?.date ? (
            <Typography>
              {formatCalendarDate(alert.date)} {alert.begin}–{alert.end}
            </Typography>
          ) : (
            (alert?.preferences || []).map((preference) => (
              <Typography key={`${preference.date}-${preference.from}`}>
                {formatCalendarDate(preference.date)} {preference.from}–{preference.to}
              </Typography>
            ))
          )}
        </Stack>
      </ScrollableDialog>

      <BookAppointmentDialog
        open={bookOpen}
        onClose={() => {
          setBookOpen(false);
          onClose?.();
        }}
        storeId={resolvedStoreId}
        teamMembers={teamMembers}
        storeDetails={storeDetails}
        storeLanguage={storeDetails?.supportedLanguages?.[0] || storeDetails?.storeLanguage}
        existingAppointments={EMPTY_APPOINTMENTS}
        initialCustomer={bookingCustomer}
        initialServiceIds={bookingServiceIds}
        onBooked={() => {
          setBookOpen(false);
          onClose?.();
        }}
      />
    </>
  );
};

export default WaitlistStaffAlertDialog;
